import React,
{
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Edit,
  TextField,
  DateField,
  ReferenceManyField,
  Datagrid,
  ReferenceArrayField,
  ArrayField,
  useRedirect,
  SingleFieldList,
  usePermissions,
  SelectField,
  ReferenceField,
  ChipField,
  WrapperField,
  useRecordContext,
  List,
  ListActions,
  TabbedShowLayout,
} from 'react-admin';

import {
  Box,
  Card,
  Grid,
  Typography,
  CardHeader,
  CardContent,
  Divider,
  styled,
  SxProps,
  useMediaQuery,
  useTheme,
  Button,
  IconButton,
} from '@mui/material';

import { blue } from '@mui/material/colors';

import PostAddIcon from '@mui/icons-material/PostAdd';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import AutoAwesome from '@mui/icons-material/AutoAwesome';

import { Route, Routes } from 'react-router-dom';

import { DateTime } from 'luxon';
import { capitalize } from 'lodash';
import EnhancedFileField from '../../customFields/EnhancedFileField';
import UpdateCaseStatusField from '../../customFields/UpdateCaseStatusField';

import hasAccess from '../../utilities/hasAccess';
import toStockholmTime from '../../utilities/toStockholmTime';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';

import StatusField from '../../customFields/StatusField';

import CaseRequestList from './components/CaseRequestList';
import CaseBreadcrumb from './CaseBreadcrumb';
import CasePingList from './components/CasePingList';
import AddEntities from './components/AddEntities';

import useCustomEditView from '../../hooks/useCustomEditView';
import AddPing from './components/AddPing';
import CommentList from '../comment/CommentList';
import actions, { Actions } from '../../constants/actions';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import CaseAssign from './components/CaseAssign';
import casePriority from '../../constants/casePriority';
import UpdateCasePriorityField from '../../customFields/UpdateCasePriorityField';
import CaseEscalate from './components/CaseEscalate';
import EditReferenceManyField from '../../customFields/EditReferenceManyField';
import DownloadReports from '../../customFields/DownloadReports';
import CardDatagrid from '../utils/CardDatagrid';
import EntityCardContent from '../entities/EntityCardContent';
import CaseSummaryTimelineCard from './components/CaseSummaryTimelineCard';
import EditField from '../../customFields/EditField';
import caseStatuses, { CaseStatuses } from '../../constants/caseStatuses';
import SnapshotList from './components/SnapshotList';
import { attributesSchema, checkIsTypeAllowed } from '../../utilities/reports/permissions';
import CasePdfReportModal from '../../customFields/CasePdfReportModal';
import SmartDateTimeInput from '../../customFields/SmartDateTimeInput';
import reportTypes from '../../constants/reportTypes';
import { AiAnalysisDrawer, AiAnalysisAside } from './components/aiAnalysis/AiAnalysisPanel';
import useDialogStatus from '../../hooks/useDialogStatus';
import dateParse, { dateFormat } from '../utils/dateParser';
import CaseDeadline from '../../customFields/CaseDeadline';
import EditToolbar from '../layout/EditToolbar';
import AuditResourceList from '../audit/AuditResourceList';
import { ReportListDatagrid } from '../reports/ReportHistoryTable';
import EditFileField from '../../customFields/EditFileField';

const customEditSx = (isCustomView: boolean): SxProps => (
  isCustomView
    ? { '& .MuiPaper-root': { backgroundColor: 'transparent', boxShadow: 'none' } }
    : {}
);

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(blue[500]),
  backgroundColor: blue[500],
  '&:hover': {
    backgroundColor: blue[700],
  },
  '&:disabled': {
    backgroundColor: blue[300],
    color: theme.palette.getContrastText(blue[300]),
  },
  marginLeft: 4,
}));

const Empty = () => '-';

const isFutureDate = (value: string) => {
  if (toStockholmTime(value).startOf('day') < toStockholmTime(DateTime.now()).startOf('day')) return 'Date should be in the future';
  return undefined;
};

const animationSpeed = '0.3s';
const animationStyle = 'ease';
const sideBarWidth = 300;
const EditActions = ({
  open,
  openPanel,
  closePanel,
}: {
  open: boolean;
  openPanel: () => void;
  closePanel: () => void;
}) => {
  const { permissions } = usePermissions<Actions[]>();

  const canOpenCaseAgent = hasAccess(permissions, actions.ANALYSIS_GET);
  const canCreateCaseReport = hasAccess(permissions, actions.REPORT_CREATE, {
    schema: attributesSchema,
    check: checkIsTypeAllowed(reportTypes.CASE_PDF),
  });
  const canUpdatePriority = hasAccess(permissions, actions.CASE_UPDATE_PRIORITY);
  const canUpdateStatus = hasAccess(permissions, actions.CASE_UPDATE_STATUS);

  // Actions are grouped by purpose: the left pair opens something, the right pair changes the
  // Case. Each pair wraps as a unit, and its buttons only split onto two lines when the pair alone
  // is wider than the toolbar. An empty pair is not rendered so it leaves no gap.
  return (
    <EditToolbar backButtonLabel="Back to cases">
      <Box display="flex" alignItems="center" flexWrap="wrap" columnGap={6} rowGap={2}>
        {(canOpenCaseAgent || canCreateCaseReport) && (
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
            data-testid="caseHeaderOpenActions"
          >
            {canOpenCaseAgent && (
              <Button
                variant="outlined"
                onClick={openPanel}
                startIcon={<AutoAwesome />}
              >
                Case agent
              </Button>
            )}
            {canCreateCaseReport && <CasePdfReportModal />}
          </Box>
        )}
        {(canUpdatePriority || canUpdateStatus) && (
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
            data-testid="caseHeaderUpdateActions"
          >
            {canUpdatePriority && <UpdateCasePriorityField />}
            {canUpdateStatus && <UpdateCaseStatusField />}
          </Box>
        )}
      </Box>
      {canOpenCaseAgent && <AiAnalysisDrawer open={open} closeDialog={closePanel} />}
    </EditToolbar>
  );
};

type DataUpdateType = {
  scheduledAt?: Date;
  caseDeadline?: Date;
  status: CaseStatuses;
  tags: string[];
  files: {
    id?: string;
    temporaryKey?: string;
    name: string;
  }[];
}

const CaseEditContent = () => {
  const fipoListRef = useRef(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const changeSidebarStatus = () => {
    setIsSidebarClosed(!isSidebarClosed);
  };

  const validatedId = useValidateId();
  const redirect = useRedirect();
  const handleGoAmlClick = useCallback(() => {
    if (validatedId.isValid) {
      redirect(`/fipo/${validatedId.id}`);
    }
  }, [validatedId, redirect]);

  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();
  const shouldShowEditScheduledAt = useMemo(
    () => hasAccess(permissions, actions.CASE_UPDATE) && record?.status === caseStatuses.SCHEDULED,
    [permissions, record?.status],
  );
  const { isCustomView } = useCustomEditView();

  if (!validatedId.isValid || !record) return null;

  return (
    <TabbedShowLayout sx={isCustomView ? { display: 'none' } : {}}>
      <TabbedShowLayout.Tab label="Information">
        <Grid container spacing={6}>
          {(hasAccess(permissions, actions.CASE_SUMMARY_CREATE)
            || hasAccess(permissions, actions.CASE_GET_STATUS_BREAKDOWN)) && (
            <Grid size={12}>
              <CaseSummaryTimelineCard
                isSidebarClosed={isSidebarClosed}
                sideBarWidth={sideBarWidth}
              />
            </Grid>
          )}
          <Grid size={12}>
            <Box display="flex" sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
              <Box
                sx={{
                  width: isSidebarClosed && !isSmallScreen ? '0%' : { xs: '100%', md: `${sideBarWidth}px` },
                  transition: `width ${animationSpeed} ${animationStyle}`,
                }}
              >
                <Box
                  position="relative"
                >
                  <Card
                    variant="outlined"
                    sx={{
                      opacity: isSidebarClosed && !isSmallScreen ? '0%' : '100%',
                      animation: `opacity ${animationSpeed} ${animationStyle}`,
                    }}
                  >
                    <CardHeader title="Case details" />
                    <Divider />
                    <CardContent>
                      <Grid container spacing={6}>
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="smallTitle">ID</Typography>
                            <TextField source="id" label="ID" />
                          </Box>
                        </Grid>
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="smallTitle">Status</Typography>
                            <StatusField source="status" />
                          </Box>
                        </Grid>
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <EditField source="scheduledAt" type="date" showEdit={shouldShowEditScheduledAt} label="Scheduled at" />
                          </Box>
                        </Grid>
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="smallTitle">Created at</Typography>
                            <DateField source="createdAt" showTime />
                          </Box>
                        </Grid>
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="smallTitle">Closed at</Typography>
                            <DateField source="closedAt" showTime emptyText="-" />
                          </Box>
                        </Grid>
                        <Grid size={12}>
                          <Typography variant="smallTitle">Assignee</Typography>
                          <CaseAssign label="Assignee" />
                        </Grid>
                        <Grid size={12}>
                          <Typography variant="smallTitle">Escalated to</Typography>
                          <CaseEscalate label="EscalatedTo" />
                        </Grid>
                        {hasAccess(permissions, actions.TAG_GET) && (
                          <Grid size={12}>
                            <EditReferenceManyField
                              withColor
                              showEdit={hasAccess(permissions, actions.CASE_UPDATE)}
                              source="tags"
                              reference="tags"
                              label="Tags"
                            />
                          </Grid>
                        )}
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="smallTitle">Priority</Typography>
                            <SelectField
                              source="priority"
                              choices={
                                Object.values(casePriority)
                                  .map((priority) => ({
                                    name: capitalize(priority),
                                    id: priority,
                                  }))
                              }
                            />
                          </Box>
                        </Grid>
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="smallTitle">Merged into</Typography>
                            <ReferenceField source="mergedInto" reference="cases" emptyText="-">
                              <TextField source="id" label="ID" />
                            </ReferenceField>
                          </Box>
                        </Grid>
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="smallTitle">Merged from</Typography>
                            <ReferenceManyField reference="cases" target="mergedInto">
                              <SingleFieldList empty={<Empty />}>
                                <ChipField source="id" label="ID" />
                              </SingleFieldList>
                            </ReferenceManyField>
                          </Box>
                        </Grid>
                        <Grid size={12}>
                          <Box display="flex" flexDirection="column">
                            <EditField
                              fieldComponent={CaseDeadline}
                              type="dateTime"
                              source="caseDeadline"
                              label="Case deadline"
                              validate={isFutureDate}
                              showTime
                              showEdit={!!record.caseDeadline}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  {!isSmallScreen && (
                    <IconButton
                      aria-label="Open drawer"
                      size="medium"
                      onClick={changeSidebarStatus}
                      sx={{
                        color: theme.palette.grey[600],
                        backgroundColor: theme.palette.grey[200],
                        borderWidth: '1px',
                        borderColor: theme.palette.grey[300],
                        borderStyle: 'solid',
                        '&:hover': {
                          backgroundColor: theme.palette.grey[100],
                          opacity: 1,
                        },
                        position: 'absolute',
                        top: '50%',
                        right: '0%',
                        transform: `${!isSidebarClosed ? 'scaleX(1) translateX(50%)' : 'scaleX(-1) translateX(-55%)'}`,
                        zIndex: 10,
                      }}
                    >
                      <MenuOpenIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
              <Grid
                container
                spacing={6}
                sx={{
                  marginLeft: isSidebarClosed ? '0' : { xs: 0, md: '24px' },
                  width: isSidebarClosed ? '100%' : { xs: '100%', md: `calc(100% - ${sideBarWidth + 24}px)` },
                  transition: `width ${animationSpeed} ${animationStyle}`,
                }}
              >
                <Grid size={12}>
                  <Box display="flex" flexDirection="column">
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      marginBottom={3}
                      paddingTop={2}
                    >
                      <Typography variant="h6">Entities</Typography>
                      <AddEntities />
                    </Box>
                    <ArrayField source="entities" perPage={10}>
                      <CardDatagrid>
                        <EntityCardContent
                          caseId={validatedId.id}
                        />
                      </CardDatagrid>
                    </ArrayField>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <CommentList source="comments" action={actions.CASE_ADD_COMMENT} deleteAction={actions.CASE_DELETE_COMMENT} />
                </Grid>
                <Grid size={12}>
                  <CasePingList />
                </Grid>
                {hasAccess(permissions, actions.REQUEST_GET) && (<CaseRequestList />)}
                {hasAccess(permissions, actions.REPORT_LIST, {
                  schema: attributesSchema,
                  check: checkIsTypeAllowed(reportTypes.FIPO),
                }) && (
                  <Grid ref={fipoListRef} size={12}>
                    <Card variant="outlined">
                      <CardHeader
                        action={hasAccess(permissions, actions.REPORT_CREATE, {
                          schema: attributesSchema,
                          check: checkIsTypeAllowed(reportTypes.FIPO),
                        }) && (
                          <ColorButton
                            startIcon={<PostAddIcon />}
                            variant="contained"
                            onClick={handleGoAmlClick}
                          >
                            Report to police (goAML)
                          </ColorButton>
                        )}
                        title="FIPO reports"
                      />
                      <Divider />
                      <ReferenceArrayField sort={{ field: 'createdAt', order: 'DESC' }} reference="reports" source="fipoReports">
                        <Datagrid
                          sx={boldDataGridStyle}
                          bulkActionButtons={false}
                          empty={(
                            <Box padding={2}>
                              <Typography align="center">No FIPO reports available</Typography>
                            </Box>
                          )}
                        >
                          <TextField sortable={false} source="id" label="ID" />
                          <DateField sortable={false} source="createdAt" showTime />
                          <WrapperField source="files">
                            <DownloadReports />
                          </WrapperField>
                        </Datagrid>
                      </ReferenceArrayField>
                    </Card>
                  </Grid>
                )}
                {hasAccess(permissions, actions.CASE_DOWNLOAD) && (
                  <Grid size={12}>
                    <Card variant="outlined">
                      <CardHeader title="Related files" />
                      <Divider />
                      <CardContent>
                        <EditFileField
                          source="files"
                          label={false}
                          accept={{ 'application/pdf': ['.pdf'] }}
                          multiple
                          showEdit={hasAccess(permissions, actions.CASE_UPDATE)}
                        >
                          <EnhancedFileField source="id" title="name" target="_blank" parentId={validatedId.id} resource="cases" />
                        </EditFileField>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {hasAccess(permissions, actions.CASE_TRANSACTION_SNAPSHOT_LIST) && (
                  <Grid size={12}>
                    <SnapshotList />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </TabbedShowLayout.Tab>
      {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
        <TabbedShowLayout.Tab label="Audit trail">
          <AuditResourceList resourceType={resourceTypes.CASE} />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.REPORT_LIST, {
        schema: attributesSchema,
        check: checkIsTypeAllowed(reportTypes.CASE_PDF),
      }) && (
        <TabbedShowLayout.Tab label="Reports" path="reports">
          <List
            storeKey={false}
            disableSyncWithLocation
            actions={<ListActions hasCreate={false} />}
            perPage={10}
            empty={false}
            pagination={false}
            exporter={false}
            resource="reports"
            sort={{ field: 'createdAt', order: 'DESC' }}
            filters={[
              <SmartDateTimeInput
                label="From Date"
                source="dateFrom"
                parse={dateParse}
                format={dateFormat}
                sx={{
                  '& .MuiFormHelperText-root': {
                    display: 'unset',
                  },
                }}
              />,
              <SmartDateTimeInput
                label="To Date"
                source="dateTo"
                parse={dateParse}
                format={dateFormat}
                sx={{
                  '& .MuiFormHelperText-root': {
                    display: 'unset',
                  },
                }}
              />,
            ]}
            filter={{ type: reportTypes.CASE_PDF, caseId: record?.id }}
            queryOptions={{ refetchInterval: 5000 }}
          >
            <Card variant="outlined">
              <ReportListDatagrid />
            </Card>
          </List>
        </TabbedShowLayout.Tab>
      )}
    </TabbedShowLayout>
  );
};

const transform = (data: DataUpdateType) => ({
  files: data.files,
  scheduledAt: data.status === caseStatuses.SCHEDULED ? data.scheduledAt : undefined,
  caseDeadline: data.caseDeadline,
  tags: data.tags,
});

const CaseEdit = () => {
  const { isCustomView } = useCustomEditView();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const validatedId = useValidateId();

  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      sx={customEditSx(isCustomView)}
      actions={<EditActions open={open} openPanel={openDialog} closePanel={closeDialog} />}
      transform={transform}
      aside={<AiAnalysisAside open={open} closeDialog={closeDialog} />}
    >
      <CaseBreadcrumb />
      {/* Custom nested routes */}
      <Routes>
        <Route
          path="/add-ping"
          element={
            <AddPing id={validatedId.id} />
          }
        />
      </Routes>
      <CaseEditContent />
    </Edit>
  );
};

export default CaseEdit;
